/**
 * m3 echo Frontend - EBIT Dashboard Tab
 *
 * Compares module revenue to actual token costs per month,
 * only for months where the tenant has core_access booked.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { EbitMonth, EbitTenant, FxInfo, PricingGaps, billingApi } from '../../services/billing';
import { currencySymbol } from '../../utils/currency';

const SERIES_LABELS: Record<string, string> = {
  revenue: 'Umsatz',
  total_costs: 'Kosten',
  ebit: 'EBIT',
};

const EbitTab: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tenants, setTenants] = useState<EbitTenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | 'all'>('all');
  const [months, setMonths] = useState<EbitMonth[]>([]);
  const [currency, setCurrency] = useState<string>('EUR');
  const [fx, setFx] = useState<FxInfo | null>(null);
  const [pricingGaps, setPricingGaps] = useState<PricingGaps | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (tenantId?: number) => {
    try {
      setLoading(true);
      setError(null);
      const res = await billingApi.getEbit(tenantId);
      setMonths(res.data.months);
      setCurrency(res.data.currency || 'EUR');
      setFx(res.data.fx ?? null);
      setPricingGaps(res.data.pricing_gaps ?? null);
      // Only set tenants on first load (all)
      if (!tenantId) {
        setTenants(res.data.tenants);
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTenantChange = (value: string) => {
    if (value === 'all') {
      setSelectedTenantId('all');
      loadData();
    } else {
      const id = Number(value);
      setSelectedTenantId(id);
      loadData(id);
    }
  };

  // Aggregate the values the API already computed (total_costs includes the
  // shared system costs) so the cards match the "ebit" series of the chart.
  const totalRevenue = months.reduce((s, m) => s + m.revenue, 0);
  const totalCosts = months.reduce((s, m) => s + m.total_costs, 0);
  const totalEbit = months.reduce((s, m) => s + m.ebit, 0);

  const symbol = currencySymbol(currency);
  const formatMoney = (value: number, digits = 2) => `${value.toFixed(digits)} ${symbol}`;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Mandant"
          size="small"
          value={selectedTenantId}
          onChange={(e) => handleTenantChange(e.target.value)}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="all">Alle Mandanten</MenuItem>
          {tenants.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.display_name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : months.length === 0 ? (
        <Alert severity="info">
          Keine Daten vorhanden. Es müssen Mandanten mit aktivem Core-Modul existieren.
        </Alert>
      ) : (
        <>
          {/* Summary cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary">Umsatz (gesamt)</Typography>
              <Typography variant="h6" color="success.main">{formatMoney(totalRevenue)}</Typography>
            </Box>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary">Kosten (gesamt)</Typography>
              <Typography variant="h6" color="error.main">{formatMoney(totalCosts)}</Typography>
            </Box>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary">EBIT (gesamt)</Typography>
              <Typography variant="h6" color={totalEbit >= 0 ? 'success.main' : 'error.main'}>
                {formatMoney(totalEbit)}
              </Typography>
            </Box>
          </Box>

          {fx && fx.is_default_rate && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Kein Umrechnungskurs konfiguriert: {fx.source_currency}-Modellpreise werden
              1:1 als {fx.target_currency} ausgewiesen. Kosten und EBIT sind erst nach dem
              Setzen von REPORTING_FX_USD_TO_EUR belastbar.
            </Alert>
          )}
          {fx && !fx.is_default_rate && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Kosten umgerechnet mit {fx.source_currency}/{fx.target_currency} ={' '}
              {fx.rate}
              {fx.rate_date ? ` (Stand ${fx.rate_date})` : ' (kein Stichtag hinterlegt)'}
            </Typography>
          )}
          {pricingGaps && pricingGaps.records > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {pricingGaps.records} Nutzungsdatensätze ohne gültigen Preis zum Nutzungsdatum
              ({pricingGaps.models.join(', ')}) — die Kosten sind eine Untergrenze.
            </Alert>
          )}

          {/* Chart */}
          <ResponsiveContainer width="100%" height={isMobile ? 280 : 400}>
            <BarChart data={months} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                tickFormatter={(v: string) => {
                  const [y, m] = v.split('-');
                  return `${m}/${y.slice(2)}`;
                }}
              />
              <YAxis tickFormatter={(v: number) => formatMoney(v, 0)} />
              <Tooltip
                formatter={(value: number, name: string) => [
                  formatMoney(value),
                  SERIES_LABELS[name] ?? name,
                ]}
                labelFormatter={(label: string) => {
                  const [y, m] = label.split('-');
                  return `${m}/${y}`;
                }}
              />
              {!isMobile && (
                <Legend formatter={(value: string) => SERIES_LABELS[value] ?? value} />
              )}
              <ReferenceLine y={0} stroke="#666" />
              <Bar dataKey="revenue" fill={theme.palette.info.main} radius={[4, 4, 0, 0]} />
              <Bar dataKey="total_costs" fill={theme.palette.error.main} radius={[4, 4, 0, 0]} />
              <Bar dataKey="ebit" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </Box>
  );
};

export default EbitTab;
